import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Book, Users, User, Plane, Heart, Clock, MapPin, Navigation, Save, Search, X } from 'lucide-react';
import { quranSurahs } from '../utils/quranData';
import { getCurrentLocation } from '../utils/prayerTimes';
import { getTranslation } from '../utils/translations';
import { getIconColor, getFocusRingColor, getBorderColor, getButtonStyles } from '../utils/themeUtils';
import type { Prayer, Language, Theme } from '../types';

interface PrayerDetailsProps {
  prayer: Prayer;
  index: number;
  isDark: boolean;
  onUpdatePrayer: (index: number, updates: Partial<Prayer>) => void;
  onUpdateLocation: (index: number, location: string) => void;
  onUpdateActualTime: (index: number, time: string) => void;
  language: Language;
  theme: Theme;
  hasUnsavedChanges: boolean;
  isSyncing: boolean;
  onSave: () => void;
  saveSuccess?: boolean;
}

interface SurahSelectorProps {
  value: string;
  onChange: (surahNumber: number) => void;
  onClear: () => void;
  isDark: boolean;
  theme: Theme;
  placeholder: string;
}

function SurahSelector({ value, onChange, onClear, isDark, theme, placeholder }: SurahSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const currentSurahNumber = useMemo(() => (value ? parseInt(value.split('.')[0], 10) : null), [value]);

  const selectedSurah = useMemo(() =>
    currentSurahNumber ? quranSurahs.find(s => s.number === currentSurahNumber) : null,
    [currentSurahNumber]
  );

  // Keep the visible text in sync when the selection changes from outside (e.g. Clear)
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm(selectedSurah ? `${selectedSurah.number}. ${selectedSurah.englishName}` : '');
    }
  }, [selectedSurah, isOpen]);

  const filteredSurahs = useMemo(() => {
    const raw = searchTerm.trim();
    if (!raw) return quranSurahs;
    // Strip a leading "12. " prefix that comes from the synced display value
    const q = raw.toLowerCase().replace(/^\d+\.\s*/, '');
    if (!q) return quranSurahs;
    return quranSurahs.filter(surah =>
      surah.englishName.toLowerCase().includes(q) ||
      surah.name.includes(raw) ||
      surah.number.toString() === q ||
      surah.number.toString().includes(q)
    );
  }, [searchTerm]);

  const handleSelect = (surahNumber: number) => {
    onChange(surahNumber);
    setIsOpen(false);
  };

  const handleClear = () => {
    onClear();
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Select text so typing immediately starts a fresh search
    if (selectedSurah) {
      setSearchTerm('');
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const displayValue = isOpen ? searchTerm : (selectedSurah ? `${selectedSurah.number}. ${selectedSurah.englishName}` : '');

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          placeholder={placeholder}
          className={`w-full pl-10 ${selectedSurah ? 'pr-9' : 'pr-3'} py-2 rounded-lg border text-sm ${
            isDark
              ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          } focus:outline-none focus:ring-2`}
          style={getFocusRingColor(theme) as React.CSSProperties}
        />
        {selectedSurah && (
          <button
            type="button"
            onClick={handleClear}
            title="Clear surah"
            aria-label="Clear surah"
            className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
              isDark ? 'text-gray-300 hover:bg-gray-500 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {isOpen && (
        <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg ${isDark ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'}`}>
          <ul className="max-h-60 overflow-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredSurahs.length > 0 ? (
              filteredSurahs.map(surah => {
                const isSelected = surah.number === currentSurahNumber;
                return (
                  <li
                    key={surah.number}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(surah.number);
                    }}
                    className={`cursor-pointer select-none relative py-2 px-3 flex items-center gap-2 ${
                      isSelected
                        ? isDark ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-900'
                        : isDark ? 'text-white hover:bg-gray-600' : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-7 h-7 rounded-full text-xs flex items-center justify-center font-medium ${
                      isSelected ? 'bg-emerald-500 text-white' : isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {surah.number}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium">{surah.englishName}</span>
                      <span className={`block truncate text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{surah.verses} verses</span>
                    </span>
                    <span className={`flex-shrink-0 text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{surah.name}</span>
                  </li>
                );
              })
            ) : (
              <li className="cursor-default select-none relative py-2 px-3 text-gray-500 text-sm">
                No surahs found. Try another name or number.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

interface VerseInputProps {
  id: string;
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  hint: string;
  isDark: boolean;
  theme: Theme;
  onChange: (value: number | undefined) => void;
}

function VerseInput({ id, label, value, min, max, hint, isDark, theme, onChange }: VerseInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(undefined);
      return;
    }
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    onChange(Math.min(Math.max(n, 1), max));
  };

  return (
    <div className="min-w-0">
      <label htmlFor={id} className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        onChange={handleChange}
        placeholder={hint}
        className={`w-full px-3 py-2 rounded-lg border text-sm ${
          isDark
            ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
        } focus:outline-none focus:ring-2`}
        style={getFocusRingColor(theme) as React.CSSProperties}
      />
      <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>1–{max}</p>
    </div>
  );
}

interface SurahRakaatFieldProps {
  title: string;
  index: number;
  memoryValue: 'remember' | 'forgot' | 'not-read' | undefined;
  surahValue: string | undefined;
  verseStart: number | undefined;
  verseEnd: number | undefined;
  radioName: string;
  isDark: boolean;
  theme: Theme;
  getMemoryOptionLabel: (option: string) => string;
  onMemoryChange: (value: 'remember' | 'forgot' | 'not-read') => void;
  onSurahChange: (surahNumber: number) => void;
  onClearSurah: () => void;
  onVerseChange: (field: 'start' | 'end', value: number | undefined) => void;
}

function SurahRakaatField({
  title,
  index,
  memoryValue,
  surahValue,
  verseStart,
  verseEnd,
  radioName,
  isDark,
  theme,
  getMemoryOptionLabel,
  onMemoryChange,
  onSurahChange,
  onClearSurah,
  onVerseChange,
}: SurahRakaatFieldProps) {
  const selectedSurah = useMemo(() => {
    if (!surahValue) return null;
    const number = parseInt(surahValue.split('.')[0], 10);
    if (isNaN(number)) return null;
    return quranSurahs.find(s => s.number === number) ?? null;
  }, [surahValue]);

  const start = verseStart ?? 1;
  const end = verseEnd ?? selectedSurah?.verses ?? 1;
  const isRangeInvalid = !!selectedSurah && start > end;

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium mb-2">
        <Book className="w-4 h-4" style={getIconColor(theme)} />
        {title}
      </label>

      {/* Memory Options */}
      <div className="flex flex-wrap gap-4 mb-3" role="radiogroup" aria-label={title}>
        {['remember', 'forgot', 'not-read'].map((option) => (
          <label key={option} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`${radioName}-${index}`}
              value={option}
              checked={memoryValue === option}
              onChange={(e) => onMemoryChange(e.target.value as 'remember' | 'forgot' | 'not-read')}
              style={{ accentColor: getIconColor(theme).color }}
            />
            <span className="text-sm">{getMemoryOptionLabel(option)}</span>
          </label>
        ))}
      </div>

      {/* Surah + verse range — only when "remember" */}
      {memoryValue === 'remember' && (
        <div className="space-y-2">
          <SurahSelector
            value={surahValue || ''}
            onChange={onSurahChange}
            onClear={onClearSurah}
            isDark={isDark}
            theme={theme}
            placeholder="Search surah by name or number…"
          />

          {selectedSurah && (
            <>
              <div className="grid grid-cols-2 gap-2 items-start">
                <VerseInput
                  id={`${radioName}-start-${index}`}
                  label="Ayat awal"
                  value={verseStart}
                  min={1}
                  max={selectedSurah.verses}
                  hint="Dari ayat"
                  isDark={isDark}
                  theme={theme}
                  onChange={(v) => onVerseChange('start', v)}
                />
                <VerseInput
                  id={`${radioName}-end-${index}`}
                  label="Ayat akhir"
                  value={verseEnd}
                  min={start}
                  max={selectedSurah.verses}
                  hint="Sampai ayat"
                  isDark={isDark}
                  theme={theme}
                  onChange={(v) => onVerseChange('end', v)}
                />
              </div>
              {isRangeInvalid ? (
                <p className="text-xs text-red-500">Ayat awal tidak boleh lebih besar dari ayat akhir.</p>
              ) : (
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-medium">{selectedSurah.name}</span>
                  {' · '}Ayat {start}–{end} dari {selectedSurah.verses} ayat
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function PrayerDetails({ 
  prayer, 
  index, 
  isDark, 
  onUpdatePrayer, 
  onUpdateLocation,
  onUpdateActualTime,
  language, 
  theme,
  hasUnsavedChanges,
  isSyncing,
  onSave,
  saveSuccess
}: PrayerDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingField, setEditingField] = useState<'location' | 'time' | null>(null);

  // Auto-hide details after successful save (user request)
  useEffect(() => {
    if (saveSuccess) {
      setIsExpanded(false);
      setEditingField(null);
    }
  }, [saveSuccess]);

  // Only show prayer details if prayer is completed
  if (!prayer.completed) {
    return null;
  }

  const handleSurahMemoryChange = (
    field: 'surahFirstMemory' | 'surahSecondMemory',
    value: 'remember' | 'forgot' | 'not-read'
  ) => {
    const updates: Partial<Prayer> = { [field]: value };
    
    // Clear surah data if not remembering
    if (value !== 'remember') {
      if (field === 'surahFirstMemory') {
        updates.surahFirst = undefined;
        updates.surahFirstVerseStart = undefined;
        updates.surahFirstVerseEnd = undefined;
      } else {
        updates.surahSecond = undefined;
        updates.surahSecondVerseStart = undefined;
        updates.surahSecondVerseEnd = undefined;
      }
    }
    
    onUpdatePrayer(index, updates);
  };

  const handleSurahChange = (
    field: 'surahFirst' | 'surahSecond',
    surahNumber: number
  ) => {
    if (surahNumber > 0) {
      const surah = quranSurahs.find(s => s.number === surahNumber);
      if (surah) {
        if (field === 'surahFirst') {
          onUpdatePrayer(index, {
            [field]: `${surah.number}. ${surah.englishName}`,
            surahFirstVerseStart: 1,
            surahFirstVerseEnd: surah.verses,
          });
        } else {
          onUpdatePrayer(index, {
            [field]: `${surah.number}. ${surah.englishName}`,
            surahSecondVerseStart: 1,
            surahSecondVerseEnd: surah.verses,
          });
        }
      }
    }
  };

  const handleClearSurah = (field: 'surahFirst' | 'surahSecond') => {
    if (field === 'surahFirst') {
      onUpdatePrayer(index, {
        surahFirst: undefined,
        surahFirstVerseStart: undefined,
        surahFirstVerseEnd: undefined,
      });
    } else {
      onUpdatePrayer(index, {
        surahSecond: undefined,
        surahSecondVerseStart: undefined,
        surahSecondVerseEnd: undefined,
      });
    }
  };

  const handleVerseRangeChange = (
    rakaat: 'first' | 'second',
    which: 'start' | 'end',
    rawValue: number | undefined
  ) => {
    const selection = rakaat === 'first' ? prayer.surahFirst : prayer.surahSecond;
    const surahNumber = selection ? parseInt(selection.split('.')[0], 10) : NaN;
    const surah = quranSurahs.find(s => s.number === surahNumber);
    const max = surah?.verses ?? 999;
    const value = rawValue === undefined ? undefined : Math.min(Math.max(rawValue, 1), max);

    if (rakaat === 'first') {
      if (which === 'start') {
        const nextStart = value ?? 1;
        const currentEnd = prayer.surahFirstVerseEnd;
        // Auto-fix: akhir mengikuti awal bila awal melewati akhir
        const nextEnd = currentEnd === undefined || nextStart > currentEnd ? nextStart : currentEnd;
        onUpdatePrayer(index, { surahFirstVerseStart: value, surahFirstVerseEnd: nextEnd });
      } else {
        onUpdatePrayer(index, { surahFirstVerseEnd: value });
      }
    } else {
      if (which === 'start') {
        const nextStart = value ?? 1;
        const currentEnd = prayer.surahSecondVerseEnd;
        const nextEnd = currentEnd === undefined || nextStart > currentEnd ? nextStart : currentEnd;
        onUpdatePrayer(index, { surahSecondVerseStart: value, surahSecondVerseEnd: nextEnd });
      } else {
        onUpdatePrayer(index, { surahSecondVerseEnd: value });
      }
    }
  };

  const handleTimeUpdate = (time: string) => {
    if (!time) return;
    onUpdateActualTime(index, time);
    setEditingField(null);
  };

  const handleLocationFromGPS = async () => {
    try {
      const location = await getCurrentLocation();
      const locationString = location.fullAddress || `${location.city || 'Current Location'} (${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)})`;
      onUpdateLocation(index, locationString);
      onUpdatePrayer(index, { coordinates: location });
    } catch (error) {
      console.error('Failed to get location:', error);
    }
  };

  const inputStyle = {
    ...getFocusRingColor(theme)
  } as React.CSSProperties;

  const radioStyle = {
    accentColor: getIconColor(theme).color
  };

  const getMemoryOptionLabel = (option: string) => {
    switch (option) {
      case 'remember':
        return 'Ingat';
      case 'forgot':
        return 'Tidak Ingat (Lupa)';
      case 'not-read':
        return 'Tidak Membaca';
      default:
        return option;
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left px-3 py-2 rounded-lg ${
          isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-100 hover:bg-gray-200'
        } transition-colors text-sm`}
      >
        {isExpanded 
          ? getTranslation('prayer.details.hide', language)
          : getTranslation('prayer.details.show', language)
        }
      </button>

      {isExpanded && (
        <div className="space-y-4 p-3 rounded-lg border" style={getBorderColor(theme)}>
          {/* Location Setting */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <MapPin className="w-4 h-4" style={getIconColor(theme)} />
              {getTranslation('ui.location', language)}
            </label>
            {editingField === 'location' ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={prayer.location}
                  onChange={(e) => onUpdateLocation(index, e.target.value)}
                  onBlur={() => setEditingField(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingField(null);
                    }
                  }}
                  autoFocus
                  placeholder="Enter mosque/location"
                  className={`flex-1 px-3 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2`}
                  style={inputStyle}
                />
                <button
                  onClick={handleLocationFromGPS}
                  className="px-3 py-2 rounded-lg text-white flex items-center gap-2"
                  style={getButtonStyles(theme)}
                  title="Use current GPS location"
                >
                  <Navigation className="w-4 h-4" />
                  GPS
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div
                  onClick={() => setEditingField('location')}
                  className={`flex-1 px-3 py-2 rounded-lg border cursor-pointer ${
                    isDark 
                      ? 'bg-gray-600 border-gray-500 hover:bg-gray-500' 
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  } transition-colors`}
                >
                  <span className={`text-sm ${
                    prayer.location 
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : 'text-gray-400'
                  }`}>
                    {prayer.location || 'Click to add location'}
                  </span>
                </div>
                <button
                  onClick={handleLocationFromGPS}
                  className="px-3 py-2 rounded-lg text-white flex items-center gap-2"
                  style={getButtonStyles(theme)}
                  title="Use current GPS location"
                >
                  <Navigation className="w-4 h-4" />
                  GPS
                </button>
              </div>
            )}
          </div>

          {/* Actual Prayer Time Setting */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Clock className="w-4 h-4" style={getIconColor(theme)} />
              Actual Prayer Time
            </label>
            {editingField === 'time' ? (
              <input
                type="time"
                value={prayer.actualTime || ''}
                onChange={(e) => handleTimeUpdate(e.target.value)}
                onBlur={() => setEditingField(null)}
                autoFocus
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-600 border-gray-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2`}
                style={inputStyle}
              />
            ) : (
              <div
                onClick={() => setEditingField('time')}
                className={`w-full px-3 py-2 rounded-lg border cursor-pointer ${
                  isDark 
                    ? 'bg-gray-600 border-gray-500 hover:bg-gray-500' 
                    : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                } transition-colors`}
              >
                <span className={`text-sm ${
                  prayer.actualTime 
                    ? isDark ? 'text-white' : 'text-gray-900'
                    : 'text-gray-400'
                }`}>
                  {prayer.actualTime 
                    ? `Prayed at ${prayer.actualTime}`
                    : 'Click to set actual prayer time'}
                </span>
              </div>
            )}
          </div>

          {/* Surah Selection for Rakaat 1 */}
          <SurahRakaatField
            title="Surah Rakaat 1"
            index={index}
            memoryValue={prayer.surahFirstMemory}
            surahValue={prayer.surahFirst}
            verseStart={prayer.surahFirstVerseStart}
            verseEnd={prayer.surahFirstVerseEnd}
            radioName="surahFirstMemory"
            isDark={isDark}
            theme={theme}
            getMemoryOptionLabel={getMemoryOptionLabel}
            onMemoryChange={(v) => handleSurahMemoryChange('surahFirstMemory', v)}
            onSurahChange={(n) => handleSurahChange('surahFirst', n)}
            onClearSurah={() => handleClearSurah('surahFirst')}
            onVerseChange={(which, v) => handleVerseRangeChange('first', which, v)}
          />

          {/* Surah Selection for Rakaat 2 */}
          <SurahRakaatField
            title="Surah Rakaat 2"
            index={index}
            memoryValue={prayer.surahSecondMemory}
            surahValue={prayer.surahSecond}
            verseStart={prayer.surahSecondVerseStart}
            verseEnd={prayer.surahSecondVerseEnd}
            radioName="surahSecondMemory"
            isDark={isDark}
            theme={theme}
            getMemoryOptionLabel={getMemoryOptionLabel}
            onMemoryChange={(v) => handleSurahMemoryChange('surahSecondMemory', v)}
            onSurahChange={(n) => handleSurahChange('surahSecond', n)}
            onClearSurah={() => handleClearSurah('surahSecond')}
            onVerseChange={(which, v) => handleVerseRangeChange('second', which, v)}
          />

          {/* Prayer Status */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Users className="w-4 h-4" style={getIconColor(theme)} />
              Prayer Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`prayerStatus-${index}`}
                  value="jamaah"
                  checked={prayer.prayerStatus === 'jamaah'}
                  onChange={(e) => onUpdatePrayer(index, { prayerStatus: e.target.value as 'jamaah' | 'individual' })}
                  style={radioStyle}
                />
                <span className="text-sm">{getTranslation('prayer.status.jamaah', language)}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`prayerStatus-${index}`}
                  value="individual"
                  checked={prayer.prayerStatus === 'individual'}
                  onChange={(e) => onUpdatePrayer(index, { prayerStatus: e.target.value as 'jamaah' | 'individual' })}
                  style={radioStyle}
                />
                <span className="text-sm">{getTranslation('prayer.status.individual', language)}</span>
              </label>
            </div>
          </div>

          {/* Jamaah Role (only if jamaah) */}
          {prayer.prayerStatus === 'jamaah' && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <User className="w-4 h-4" style={getIconColor(theme)} />
                Role in Congregation
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`jamaahRole-${index}`}
                    value="imam"
                    checked={prayer.jamaahRole === 'imam'}
                    onChange={(e) => onUpdatePrayer(index, { jamaahRole: e.target.value as 'imam' | 'makmum' })}
                    style={radioStyle}
                  />
                  <span className="text-sm">Imam</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`jamaahRole-${index}`}
                    value="makmum"
                    checked={prayer.jamaahRole === 'makmum'}
                    onChange={(e) => onUpdatePrayer(index, { jamaahRole: e.target.value as 'imam' | 'makmum' })}
                    style={radioStyle}
                  />
                  <span className="text-sm">Follower</span>
                </label>
              </div>
            </div>
          )}

          {/* Travel Status */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Plane className="w-4 h-4" style={getIconColor(theme)} />
              Travel Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`travelStatus-${index}`}
                  value="mukim"
                  checked={prayer.travelStatus === 'mukim'}
                  onChange={(e) => onUpdatePrayer(index, { travelStatus: e.target.value as 'mukim' | 'safar' })}
                  style={radioStyle}
                />
                <span className="text-sm">Resident</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`travelStatus-${index}`}
                  value="safar"
                  checked={prayer.travelStatus === 'safar'}
                  onChange={(e) => onUpdatePrayer(index, { travelStatus: e.target.value as 'mukim' | 'safar' })}
                  style={radioStyle}
                />
                <span className="text-sm">Traveling</span>
              </label>
            </div>
          </div>

          {/* Health Status */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Heart className="w-4 h-4" style={getIconColor(theme)} />
              Health Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`healthStatus-${index}`}
                  value="sehat"
                  checked={prayer.healthStatus === 'sehat'}
                  onChange={(e) => onUpdatePrayer(index, { healthStatus: e.target.value as 'sehat' | 'sakit' })}
                  style={radioStyle}
                />
                <span className="text-sm">Healthy</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`healthStatus-${index}`}
                  value="sakit"
                  checked={prayer.healthStatus === 'sakit'}
                  onChange={(e) => onUpdatePrayer(index, { healthStatus: e.target.value as 'sehat' | 'sakit' })}
                  style={radioStyle}
                />
                <span className="text-sm">Sick</span>
              </label>
            </div>
          </div>

          {/* Sick Position (only if sick) */}
          {prayer.healthStatus === 'sakit' && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Heart className="w-4 h-4 text-red-500" />
                Prayer Position
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`sickPosition-${index}`}
                    value="duduk"
                    checked={prayer.sickPosition === 'duduk'}
                    onChange={(e) => onUpdatePrayer(index, { sickPosition: e.target.value as 'duduk' | 'tidur' })}
                    style={radioStyle}
                  />
                  <span className="text-sm">Sitting</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`sickPosition-${index}`}
                    value="tidur"
                    checked={prayer.sickPosition === 'tidur'}
                    onChange={(e) => onUpdatePrayer(index, { sickPosition: e.target.value as 'duduk' | 'tidur' })}
                    style={radioStyle}
                  />
                  <span className="text-sm">Lying Down</span>
                </label>
              </div>
            </div>
          )}

          {/* Time Status */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Clock className="w-4 h-4" style={getIconColor(theme)} />
              Time Status
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`timeStatus-${index}`}
                  value="ontime"
                  checked={prayer.timeStatus === 'ontime'}
                  onChange={(e) => onUpdatePrayer(index, { timeStatus: e.target.value as 'ontime' | 'late' | 'qodo' })}
                  style={radioStyle}
                />
                <span className="text-sm">On Time</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`timeStatus-${index}`}
                  value="late"
                  checked={prayer.timeStatus === 'late'}
                  onChange={(e) => onUpdatePrayer(index, { timeStatus: e.target.value as 'ontime' | 'late' | 'qodo' })}
                  style={radioStyle}
                />
                <span className="text-sm">Late</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`timeStatus-${index}`}
                  value="qodo"
                  checked={prayer.timeStatus === 'qodo'}
                  onChange={(e) => onUpdatePrayer(index, { timeStatus: e.target.value as 'ontime' | 'late' | 'qodo' })}
                  style={radioStyle}
                />
                <span className="text-sm">Qodo</span>
              </label>
            </div>
          </div>

          {/* Save Button - Only show when there are unsaved changes */}
          {hasUnsavedChanges && (
            <div className="pt-4 border-t" style={{ borderTopColor: getBorderColor(theme).borderColor }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                  <span className="text-sm text-orange-600 dark:text-orange-400">
                    {getTranslation('save.unsavedChanges', language)}
                  </span>
                </div>
                <button
                  onClick={onSave}
                  disabled={isSyncing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
                  style={getButtonStyles(theme)}
                >
                  {isSyncing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">{getTranslation('save.saving', language)}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span className="text-sm">{getTranslation('save.saveChanges', language)}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}